<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import V4Button from 'dashboard/components-next/button/Button.vue';

export default {
  name: 'TicketsSettings',
  components: {
    Icon,
    V4Button,
  },
  data() {
    return {
      newService: { name: '' },
      newCategory: { name: '' },
      newUrgency: { name: '', color: '#ef4444' },

      ticketServices: [],
      ticketCategories: [],
      ticketUrgencies: [],
      autoCreateTicketOnResolve: false,
      autoCloseTicketDays: 0,
    };
  },
  computed: {
    ...mapGetters({
      currentAccountId: 'getCurrentAccountId',
      getAccount: 'accounts/getAccount',
    }),
    currentAccount() {
      return this.getAccount(this.currentAccountId);
    },
    apiBaseUrl() {
      return `${window.location.origin}/api/v1/accounts/${this.currentAccountId}/tickets`;
    },
    defaultUrgencies() {
      return [
        {
          name: this.$t('TICKET_SETTINGS.URGENCY_LOW'),
          color: '#3b82f6',
        },
        {
          name: this.$t('TICKET_SETTINGS.URGENCY_MEDIUM'),
          color: '#eab308',
        },
        {
          name: this.$t('TICKET_SETTINGS.URGENCY_HIGH'),
          color: '#f97316',
        },
        {
          name: this.$t('TICKET_SETTINGS.URGENCY_URGENT'),
          color: '#ef4444',
        },
      ];
    },
  },
  watch: {
    currentAccount: {
      immediate: true,
      handler(account) {
        if (account) {
          this.autoCreateTicketOnResolve =
            account.settings?.auto_create_ticket_on_resolve || false;
          this.autoCloseTicketDays =
            account.settings?.auto_close_ticket_days || 0;
        }

        if (account && account.custom_attributes) {
          this.ticketServices = Array.isArray(
            account.custom_attributes.ticket_services
          )
            ? [...account.custom_attributes.ticket_services]
            : [];
          this.ticketCategories = Array.isArray(
            account.custom_attributes.ticket_categories
          )
            ? [...account.custom_attributes.ticket_categories]
            : [];

          let urgencies = Array.isArray(
            account.custom_attributes.ticket_urgencies
          )
            ? [...account.custom_attributes.ticket_urgencies]
            : [];
          if (urgencies.length === 0) {
            this.ticketUrgencies = this.defaultUrgencies;
            return;
          }

          this.ticketUrgencies = this.normalizeDefaultUrgencies(urgencies);
        }
      },
    },
  },
  methods: {
    addService() {
      if (!this.newService.name.trim()) return;
      this.ticketServices.push({ ...this.newService });
      this.newService = { name: '' };
      this.saveConfiguration();
    },
    removeService(index) {
      this.ticketServices.splice(index, 1);
      this.saveConfiguration();
    },
    addCategory() {
      if (!this.newCategory.name.trim()) return;
      this.ticketCategories.push({ ...this.newCategory });
      this.newCategory = { name: '' };
      this.saveConfiguration();
    },
    removeCategory(index) {
      this.ticketCategories.splice(index, 1);
      this.saveConfiguration();
    },
    addUrgency() {
      if (!this.newUrgency.name.trim()) return;
      this.ticketUrgencies.push({ ...this.newUrgency });
      this.newUrgency = { name: '', color: '#ef4444' };
      this.saveConfiguration();
    },
    removeUrgency(index) {
      this.ticketUrgencies.splice(index, 1);
      this.saveConfiguration();
    },
    normalizeDefaultUrgencies(urgencies) {
      const defaultColors = this.defaultUrgencies.map(urgency => urgency.color);
      const urgencyColors = urgencies.map(urgency => urgency.color);
      const isDefaultSet =
        urgencies.length === this.defaultUrgencies.length &&
        new Set(urgencyColors).size === defaultColors.length &&
        defaultColors.every(color => urgencyColors.includes(color));

      if (!isDefaultSet) return urgencies;

      return urgencies.map(urgency => {
        const defaultUrgency = this.defaultUrgencies.find(
          item => item.color === urgency.color
        );

        return { ...urgency, name: defaultUrgency.name };
      });
    },
    isDefaultUrgency(urgency) {
      return this.defaultUrgencies.some(
        defaultUrgency =>
          defaultUrgency.name === urgency.name &&
          defaultUrgency.color === urgency.color
      );
    },
    async saveConfiguration() {
      if (this.autoCloseTicketDays > 10) {
        this.autoCloseTicketDays = 10;
      } else if (
        this.autoCloseTicketDays < 0 ||
        this.autoCloseTicketDays === ''
      ) {
        this.autoCloseTicketDays = 0;
      }

      try {
        await this.$store.dispatch('accounts/update', {
          auto_create_ticket_on_resolve: this.autoCreateTicketOnResolve,
          auto_close_ticket_days: this.autoCloseTicketDays,
          custom_attributes: {
            ticket_services: this.ticketServices,
            ticket_categories: this.ticketCategories,
            ticket_urgencies: this.ticketUrgencies,
          },
        });
        useAlert(this.$t('TICKET_SETTINGS.SAVE_SUCCESS'));
      } catch (error) {
        useAlert(this.$t('TICKET_SETTINGS.SAVE_ERROR'));
      }
    },
  },
};
</script>

<template>
  <div class="flex flex-col lg:flex-row gap-8 w-full h-full overflow-y-auto">
    <div class="flex-1 bg-white dark:bg-slate-900 p-8 w-full">
      <div class="max-w-4xl mx-auto">
        <div class="mb-8 border-b border-slate-200 dark:border-slate-700 pb-6">
          <h2 class="text-2xl font-bold text-slate-900 dark:text-white mb-2">
            {{ $t('TICKET_SETTINGS.TITLE') }}
          </h2>
          <p class="text-sm text-slate-500">
            {{ $t('TICKET_SETTINGS.DESCRIPTION') }}
          </p>
        </div>

        <div class="space-y-10">
          <!-- Opções Gerais -->
          <section>
            <h3
              class="text-lg font-semibold text-slate-900 dark:text-white mb-4"
            >
              {{ $t('TICKET_SETTINGS.GENERAL_OPTIONS') }}
            </h3>

            <div
              class="bg-slate-50 dark:bg-slate-800 rounded-xl px-6 py-4 border border-slate-200 dark:border-slate-700"
            >
              <div class="flex items-center gap-2">
                <input
                  id="autoCreateTicket"
                  v-model="autoCreateTicketOnResolve"
                  type="checkbox"
                  class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                  @change="saveConfiguration"
                />
                <label
                  for="autoCreateTicket"
                  class="text-sm font-medium text-slate-700 dark:text-slate-300 cursor-pointer"
                >
                  {{ $t('GENERAL_SETTINGS.FORM.AUTO_CREATE_TICKET') }}
                </label>
              </div>
              <div class="mt-4 flex items-center gap-3">
                <label
                  for="autoCloseTicketDays"
                  class="text-sm font-medium text-slate-700 dark:text-slate-300"
                >
                  {{ $t('TICKET_SETTINGS.AUTO_CLOSE_AFTER') }}
                </label>
                <div class="flex items-center gap-2">
                  <input
                    id="autoCloseTicketDays"
                    v-model="autoCloseTicketDays"
                    type="number"
                    min="0"
                    max="10"
                    class="w-20 px-3 py-1.5 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-woot-500 dark:text-white"
                    @change="saveConfiguration"
                  />
                  <span
                    class="text-sm text-slate-500 dark:text-slate-400 whitespace-nowrap"
                    >{{ $t('TICKET_SETTINGS.DAYS_NEVER') }}</span
                  >
                </div>
              </div>
            </div>
          </section>

          <!-- Seção Serviço -->
          <section>
            <h3
              class="text-lg font-semibold text-slate-900 dark:text-white mb-4"
            >
              {{ $t('TICKET_SETTINGS.SERVICE_OPTIONS') }}
            </h3>

            <div
              class="bg-slate-50 dark:bg-slate-800 rounded-xl p-6 border border-slate-200 dark:border-slate-700"
            >
              <div class="flex items-center gap-3 mb-6">
                <input
                  v-model="newService.name"
                  type="text"
                  :placeholder="$t('TICKET_SETTINGS.SERVICE_NAME')"
                  class="flex-1 px-3 !m-0 h-10 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-woot-500 dark:text-white"
                  @keyup.enter="addService"
                />
                <V4Button
                  :disabled="!newService.name.trim()"
                  @click="addService"
                >
                  {{ $t('TICKET_SETTINGS.ADD') }}
                </V4Button>
              </div>

              <div v-if="ticketServices.length > 0" class="space-y-2">
                <div
                  v-for="(svc, index) in ticketServices"
                  :key="index"
                  class="flex items-center justify-between bg-white dark:bg-slate-900 p-3 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm"
                >
                  <div class="flex items-center gap-3">
                    <span
                      class="text-sm font-medium text-slate-700 dark:text-slate-300"
                      >{{ svc.name }}</span
                    >
                  </div>
                  <button
                    class="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                    :title="$t('TICKET_SETTINGS.REMOVE')"
                    @click="removeService(index)"
                  >
                    <Icon icon="i-lucide-trash-2" class="size-4" />
                  </button>
                </div>
              </div>
              <p v-else class="text-sm text-slate-500 italic">
                {{ $t('TICKET_SETTINGS.NO_SERVICES') }}
              </p>
            </div>
          </section>

          <!-- Seção Categoria -->
          <section>
            <h3
              class="text-lg font-semibold text-slate-900 dark:text-white mb-4"
            >
              {{ $t('TICKET_SETTINGS.CATEGORY_OPTIONS') }}
            </h3>

            <div
              class="bg-slate-50 dark:bg-slate-800 rounded-xl p-6 border border-slate-200 dark:border-slate-700"
            >
              <div class="flex items-center gap-3 mb-6">
                <input
                  v-model="newCategory.name"
                  type="text"
                  :placeholder="$t('TICKET_SETTINGS.CATEGORY_NAME')"
                  class="flex-1 px-3 !m-0 h-10 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-woot-500 dark:text-white"
                  @keyup.enter="addCategory"
                />
                <V4Button
                  :disabled="!newCategory.name.trim()"
                  @click="addCategory"
                >
                  {{ $t('TICKET_SETTINGS.ADD') }}
                </V4Button>
              </div>

              <div v-if="ticketCategories.length > 0" class="space-y-2">
                <div
                  v-for="(cat, index) in ticketCategories"
                  :key="index"
                  class="flex items-center justify-between bg-white dark:bg-slate-900 p-3 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm"
                >
                  <div class="flex items-center gap-3">
                    <span
                      class="text-sm font-medium text-slate-700 dark:text-slate-300"
                      >{{ cat.name }}</span
                    >
                  </div>
                  <button
                    class="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                    :title="$t('TICKET_SETTINGS.REMOVE')"
                    @click="removeCategory(index)"
                  >
                    <Icon icon="i-lucide-trash-2" class="size-4" />
                  </button>
                </div>
              </div>
              <p v-else class="text-sm text-slate-500 italic">
                {{ $t('TICKET_SETTINGS.NO_CATEGORIES') }}
              </p>
            </div>
          </section>

          <!-- Seção Urgência -->
          <section>
            <h3
              class="text-lg font-semibold text-slate-900 dark:text-white mb-4"
            >
              {{ $t('TICKET_SETTINGS.URGENCY_OPTIONS') }}
            </h3>

            <div
              class="bg-slate-50 dark:bg-slate-800 rounded-xl p-6 border border-slate-200 dark:border-slate-700"
            >
              <div class="flex items-center gap-3 mb-6">
                <input
                  v-model="newUrgency.name"
                  type="text"
                  :placeholder="$t('TICKET_SETTINGS.URGENCY_NAME')"
                  class="flex-1 px-3 !m-0 h-10 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-woot-500 dark:text-white"
                  @keyup.enter="addUrgency"
                />
                <input
                  v-model="newUrgency.color"
                  type="color"
                  class="w-10 h-10 !m-0 p-1 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg cursor-pointer"
                />
                <V4Button
                  :disabled="!newUrgency.name.trim()"
                  @click="addUrgency"
                >
                  {{ $t('TICKET_SETTINGS.ADD') }}
                </V4Button>
              </div>

              <div v-if="ticketUrgencies.length > 0" class="space-y-2">
                <div
                  v-for="(urg, index) in ticketUrgencies"
                  :key="index"
                  class="flex items-center justify-between bg-white dark:bg-slate-900 p-3 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm"
                >
                  <div class="flex items-center gap-3">
                    <div
                      class="w-4 h-4 rounded-full"
                      :style="{ backgroundColor: urg.color || '#cbd5e1' }"
                    />
                    <span
                      class="text-sm font-medium text-slate-700 dark:text-slate-300"
                      >{{ urg.name }}</span
                    >
                  </div>
                  <button
                    v-if="!isDefaultUrgency(urg)"
                    class="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                    :title="$t('TICKET_SETTINGS.REMOVE')"
                    @click="removeUrgency(index)"
                  >
                    <Icon icon="i-lucide-trash-2" class="size-4" />
                  </button>
                </div>
              </div>
              <p v-else class="text-sm text-slate-500 italic">
                {{ $t('TICKET_SETTINGS.NO_URGENCIES') }}
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>

    <div class="hidden lg:flex w-[460px] flex-col shrink-0 gap-4 mt-8 pr-8">
      <div
        class="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-6 shadow-sm"
      >
        <div class="flex items-center gap-2 mb-4">
          <div class="text-slate-900 dark:text-white">
            <Icon icon="i-lucide-braces" class="text-xl" />
          </div>
          <h3 class="text-lg text-slate-900 dark:text-white font-semibold m-0">
            {{ $t('TICKET_SETTINGS.API_TITLE') }}
          </h3>
        </div>

        <p class="text-sm text-slate-600 dark:text-slate-300 mb-4">
          {{ $t('TICKET_SETTINGS.API_DESCRIPTION') }}
        </p>

        <div
          class="bg-slate-900 rounded-lg p-4 font-mono text-xs text-slate-300 break-all"
        >
          <div class="mb-1 text-slate-500">
            {{ $t('TICKET_SETTINGS.API_LIST') }}
          </div>
          <div class="mb-4">
            <span class="text-emerald-400">{{
              $t('TICKET_SETTINGS.GET')
            }}</span>
            {{ apiBaseUrl }}
          </div>

          <div class="mb-1 text-slate-500">
            {{ $t('TICKET_SETTINGS.API_SHOW') }}
          </div>
          <div class="mb-4">
            <span class="text-emerald-400">{{
              $t('TICKET_SETTINGS.GET')
            }}</span>
            {{ apiBaseUrl }}{{ $t('TICKET_SETTINGS.PATH_ID') }}
          </div>

          <div class="mb-1 text-slate-500">
            {{ $t('TICKET_SETTINGS.API_CREATE') }}
          </div>
          <div class="mb-4">
            <span class="text-blue-400">{{ $t('TICKET_SETTINGS.POST') }}</span>
            {{ apiBaseUrl }}
            <pre
              class="mt-2 text-[10px] text-slate-400 p-2 bg-slate-800 rounded border border-slate-700"
              >{{ $t('TICKET_SETTINGS.API_EXAMPLE') }}</pre
            >
          </div>

          <div class="mb-1 text-slate-500">
            {{ $t('TICKET_SETTINGS.API_UPDATE') }}
          </div>
          <div class="mb-5">
            <span class="text-orange-400">{{
              $t('TICKET_SETTINGS.PATCH')
            }}</span>
            {{ apiBaseUrl }}{{ $t('TICKET_SETTINGS.PATH_ID') }}
          </div>

          <div class="text-yellow-400 mb-1">
            {{ $t('TICKET_SETTINGS.API_HEADERS') }}
          </div>
          <div>
            {{ $t('TICKET_SETTINGS.API_ACCESS_TOKEN') }}
            <span class="text-slate-400">{{
              $t('TICKET_SETTINGS.API_TOKEN')
            }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
